/**
 * Size Model Selector Component
 * Shows model images wearing different sizes to help customers visualize fit
 */

class SizeModelSelector extends HTMLElement {
  constructor() {
    super();
    this.modal = this.querySelector('.size-model-modal');
    this.imagesPreloaded = false;
    this.initializeAvailableSizes();
    this.attachListeners();
    this.preloadImages();
  }

  initializeAvailableSizes() {
    if (!this.modal) return;
    
    const activeCard = this.modal.querySelector('.model-card[data-active="true"]');
    if (activeCard) {
      const activeSize = activeCard.dataset.modelSize;
      const activePill = this.modal.querySelector(`.size-pill[data-size="${activeSize}"]`);
      
      if (activePill && (activePill.disabled || activePill.dataset.available === 'false')) {
        const firstAvailablePill = this.modal.querySelector('.size-pill:not(.disabled):not([disabled])');
        if (firstAvailablePill) {
          const firstAvailableSize = firstAvailablePill.dataset.size;
          this.switchToSize(firstAvailableSize);
        }
      } else {
        const selectBtn = activeCard.querySelector('.select-size-btn');
        if (selectBtn && activePill) {
          const isAvailable = activePill.dataset.available === 'true';
          selectBtn.disabled = !isAvailable;
        }
      }
    }
  }

  attachListeners() {
    if (!this.modal) return;

    this.setupPreloadObserver();

    this.modal.querySelectorAll('.size-pill').forEach(pill => {
      pill.addEventListener('click', (e) => {
        if (pill.disabled || pill.dataset.available === 'false') return;
        const selectedSize = e.target.dataset.size;
        this.switchToSize(selectedSize);
      });
    });

    this.modal.querySelectorAll('.select-size-btn').forEach(btn => {
      btn.addEventListener('click', (e) => {
        if (btn.disabled) return;
        const size = e.target.dataset.selectedSize;
        this.selectSize(size);
      });
    });
  }

  setupPreloadObserver() {
    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting && !this.imagesPreloaded) {
          this.preloadImages();
          observer.disconnect();
        }
      });
    }, {
      threshold: 0.1
    });

    if (this.modal) {
      observer.observe(this.modal);
    }
  }

  preloadImages() {
    if (this.imagesPreloaded) return;
    
    const images = this.modal.querySelectorAll('.model-image');
    const preloadPromises = [];
    
    images.forEach(img => {
      if (img.complete || img.dataset.preloaded === 'true') return;
      
      const preloadImg = new Image();
      const promise = new Promise((resolve) => {
        preloadImg.onload = () => {
          img.dataset.preloaded = 'true';
          resolve();
        };
        preloadImg.onerror = () => resolve();
      });
      
      preloadImg.src = img.src;
      preloadPromises.push(promise);
    });
    
    Promise.all(preloadPromises).then(() => {
      this.normalizeImageHeights();
      this.imagesPreloaded = true;
    });
  }

  normalizeImageHeights() {
    const wrappers = this.modal.querySelectorAll('.model-image-wrapper');
    let maxHeight = 0;
    
    wrappers.forEach(wrapper => {
      const img = wrapper.querySelector('.model-image');
      if (img && img.complete) {
        const imgHeight = img.naturalHeight;
        const imgWidth = img.naturalWidth;
        if (imgHeight > 0 && imgWidth > 0) {
          const containerWidth = wrapper.offsetWidth || wrapper.clientWidth;
          if (containerWidth > 0) {
            const aspectRatio = imgWidth / imgHeight;
            const calculatedHeight = containerWidth / aspectRatio;
            maxHeight = Math.max(maxHeight, calculatedHeight);
          }
        }
      }
    });
    
    if (maxHeight > 0 && window.innerWidth < 750) {
      wrappers.forEach(wrapper => {
        wrapper.style.minHeight = `${maxHeight}px`;
      });
    }
  }

  switchToSize(size) {
    if (!this.modal) return;

    const targetPill = this.modal.querySelector(`.size-pill[data-size="${size}"]`);
    if (targetPill && (targetPill.disabled || targetPill.dataset.available === 'false')) {
      return;
    }

    this.modal.querySelectorAll('.model-card').forEach(card => {
      card.dataset.active = 'false';
    });

    const targetCard = this.modal.querySelector(`.model-card[data-model-size="${size}"]`);
    if (!targetCard) return;
    
    targetCard.dataset.active = 'true';

    this.modal.querySelectorAll('.size-pill').forEach(pill => {
      pill.classList.toggle('active', pill.dataset.size === size);
    });

    const selectBtn = targetCard.querySelector('.select-size-btn');
    if (selectBtn) {
      const isAvailable = targetPill && targetPill.dataset.available === 'true';
      selectBtn.disabled = !isAvailable;
      selectBtn.textContent = `SELECT SIZE ${size.toUpperCase()}`;
      selectBtn.dataset.selectedSize = size.toUpperCase();
    }
  }

  selectSize(size) {
    const variantSelector = document.querySelector('variant-selects, variant-radios');
    if (variantSelector) {
      const sizeOption = variantSelector.querySelector(`input[value="${size}"], option[value="${size}"]`);
      if (sizeOption) {
        if (sizeOption.disabled || sizeOption.classList.contains('disabled')) {
          return;
        }
        if (sizeOption.tagName === 'INPUT') {
          sizeOption.checked = true;
          sizeOption.dispatchEvent(new Event('change', { bubbles: true }));
        } else {
          sizeOption.selected = true;
          sizeOption.closest('select').dispatchEvent(new Event('change', { bubbles: true }));
        }
      }
    }

    if (this.modal) {
      this.modal.hide();
    }
  }
}

customElements.define('size-model-selector', SizeModelSelector);

